import React from "react";
import { useAutoScroll } from "@/hooks/useAutoScroll";
import { Link } from "react-router-dom";
import ProductCard from "./ProductCard";
import ProductCardSkeleton from "./ProductCardSkeleton";
import { PRODUCTS } from "@/data/products";
import { ArrowUpRight } from "lucide-react";
import { useShopifyProducts } from "@/hooks/useShopify";
import { SHOPIFY_CONFIGURED } from "@/lib/shopify";

const NewArrivals = () => {
  // Pull live products from Shopify (sorted by BEST_SELLING by default).
  const { products, loading, error } = useShopifyProducts({ first: 8 });

  const showLive = SHOPIFY_CONFIGURED && !error && products.length > 0;
  const items = showLive ? products : PRODUCTS.slice(0, 8);
  const showSkeletons = SHOPIFY_CONFIGURED && loading && products.length === 0;
  const track = [...items, ...items];
  const railRef = useAutoScroll(0.5, !showSkeletons && track.length > 0);

  // Duplicate the list so the marquee loops seamlessly (no visible jump)

  return (
    <section
      className="py-16 lg:py-24"
      style={{ background: "#FDF6F0" }}
      data-testid="new-arrivals"
    >

      <div className="max-w-[1440px] mx-auto px-5 lg:px-10 mb-10 lg:mb-14">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="gold-divider" />
              <span className="overline">Fresh Drops</span>
            </div>
            <h2
              className="font-serif text-plum leading-[1] tracking-tight"
              style={{ fontSize: "clamp(32px, 4.4vw, 56px)" }}
            >
              Just <span className="italic text-blush">dropped</span>
            </h2>
            <p className="mt-3 text-plum/65 max-w-[460px] text-[15px]">
              Fresh from the studio — live from our store.
            </p>
          </div>
          <Link
            to="/shop?filter=new"
            className="text-sm tracking-[0.18em] uppercase text-gold hover:text-blush flex items-center gap-1.5"
            data-testid="view-all-new-arrivals"
          >
            View all new <ArrowUpRight size={15} />
          </Link>
        </div>
      </div>

      {/* Auto-marquee rail — full-bleed, drifts left continuously */}
      {showSkeletons ? (
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-7">
            {Array.from({ length: 8 }).map((_, i) => (
              <ProductCardSkeleton key={i} />
            ))}
          </div>
        </div>
      ) : (
        <div className="relative" data-testid="just-dropped-marquee">
          <div ref={railRef} className="overflow-x-auto no-scrollbar select-none cursor-grab active:cursor-grabbing" data-testid="just-dropped-rail">
          <div className="flex gap-5 lg:gap-7 w-max pl-5 lg:pl-10">
            {track.map((p, idx) => (
              <div
                key={`${p.id}-${idx}`}
                className="w-[68vw] sm:w-[280px] lg:w-[320px] flex-shrink-0"
              >
                <ProductCard product={p} />
              </div>
            ))}
          </div>
          </div>

          {/* Soft edge fades */}
          <div
            aria-hidden
            className="pointer-events-none absolute top-0 bottom-0 left-0 w-16 lg:w-24"
            style={{
              background:
                "linear-gradient(to right, #FDF6F0 0%, rgba(253,246,240,0) 100%)",
            }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute top-0 bottom-0 right-0 w-16 lg:w-24"
            style={{
              background:
                "linear-gradient(to left, #FDF6F0 0%, rgba(253,246,240,0) 100%)",
            }}
          />
        </div>
      )}
    </section>
  );
};

export default NewArrivals;
